import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Users, TrendingUp, AlertTriangle } from 'lucide-react';

export function PoolAnalysis() {
  const { poolAddress } = useParams<{ poolAddress: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['pool', poolAddress],
    queryFn: async () => {
      const response = await fetch(`/api/pool/${poolAddress}`);
      if (!response.ok) throw new Error('Failed to fetch pool data');
      return response.json();
    },
    enabled: !!poolAddress,
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div className="loading" style={{ width: '3rem', height: '3rem' }} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
          <ArrowLeft size={18} />
          Back to Dashboard
        </Link>
        <div className="card" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderLeft: '4px solid var(--color-danger)' }}>
          <p>Pool not found or error loading data.</p>
        </div>
      </div>
    );
  }

  const { pool, addresses, relationships } = data;

  return (
    <div>
      <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
        <ArrowLeft size={18} />
        Back to Dashboard
      </Link>

      <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: 'var(--spacing-lg)' }}>
        Pool Analysis
      </h1>

      {/* Pool Overview */}
      <div className="card" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--spacing-lg)' }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: 'var(--spacing-sm)' }}>
              Pool Address
            </h2>
            <code style={{ fontSize: '0.875rem' }}>{pool.address}</code>
          </div>
          <span className="badge badge-critical">
            Risk: {pool.risk_score}/100
          </span>
        </div>

        <div className="grid grid-3">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
              <Users size={20} color="var(--color-primary)" />
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                Proxy Addresses
              </span>
            </div>
            <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{pool.total_proxies}</p>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
              <TrendingUp size={20} color="var(--color-success)" />
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                Total Volume
              </span>
            </div>
            <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
              {pool.total_volume ? `${(BigInt(pool.total_volume) / BigInt(10 ** 18)).toString()} tokens` : 'N/A'}
            </p>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
              <AlertTriangle size={20} color="var(--color-warning)" />
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                Status
              </span>
            </div>
            <p style={{ fontSize: '1.5rem', fontWeight: 'bold', textTransform: 'capitalize' }}>
              {pool.status}
            </p>
          </div>
        </div>

        {pool.notes && (
          <div style={{ marginTop: 'var(--spacing-lg)', padding: 'var(--spacing-md)', backgroundColor: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
            <p style={{ fontSize: '0.875rem' }}>{pool.notes}</p>
          </div>
        )}
      </div>

      {/* Associated Addresses */}
      <div className="card" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: 'var(--spacing-lg)' }}>
          Associated Addresses ({addresses.length})
        </h2>

        {addresses.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No associated addresses found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left' }}>Address</th>
                  <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left' }}>Risk Rating</th>
                  <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>Risk Score</th>
                  <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>Transactions</th>
                  <th style={{ padding: 'var(--spacing-sm)', textAlign: 'center' }}>Blacklisted</th>
                </tr>
              </thead>
              <tbody>
                {addresses.slice(0, 20).map((addr: any) => (
                  <tr key={addr.address} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: 'var(--spacing-sm)' }}>
                      <Link to={`/lookup?address=${addr.address}`}>
                        <code style={{ fontSize: '0.75rem' }}>
                          {addr.address.slice(0, 10)}...{addr.address.slice(-8)}
                        </code>
                      </Link>
                    </td>
                    <td style={{ padding: 'var(--spacing-sm)' }}>
                      <span className={`badge badge-${addr.risk_rating}`}>
                        {addr.risk_rating}
                      </span>
                    </td>
                    <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>
                      {addr.risk_score}
                    </td>
                    <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>
                      {addr.total_transactions}
                    </td>
                    <td style={{ padding: 'var(--spacing-sm)', textAlign: 'center' }}>
                      {addr.is_blacklisted ? '✓' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Relationships */}
      <div className="card">
        <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: 'var(--spacing-lg)' }}>
          Transaction Relationships ({relationships.length})
        </h2>

        {relationships.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No relationships found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left' }}>From</th>
                  <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left' }}>To</th>
                  <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>Transfers</th>
                  <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>Hops</th>
                </tr>
              </thead>
              <tbody>
                {relationships.slice(0, 20).map((rel: any, index: number) => (
                  <tr key={index} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: 'var(--spacing-sm)' }}>
                      <code style={{ fontSize: '0.75rem' }}>
                        {rel.source_address.slice(0, 8)}...
                      </code>
                    </td>
                    <td style={{ padding: 'var(--spacing-sm)' }}>
                      <code style={{ fontSize: '0.75rem' }}>
                        {rel.target_address.slice(0, 8)}...
                      </code>
                    </td>
                    <td style={{ padding: 'var(--spacing-sm)', textTransform: 'capitalize' }}>
                      {rel.relationship_type}
                    </td>
                    <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>
                      {rel.total_transfers}
                    </td>
                    <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>
                      {rel.hop_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PoolAnalysis;
